import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Brain,
  Check,
  FileText,
  MessageSquare,
  Mic,
  Search,
  SplitSquareHorizontal,
  Sparkles,
  Workflow,
} from 'lucide-react';
import { useRevealOnScroll } from '../../../hooks/useRevealOnScroll';
import { PRODUCT_NAME_MARKETING } from '../../../constants';
import { T05ProductShell } from './T05ProductShell';
import { T05ReferoFeatureRow } from './T05ReferoFeatureRow';
import { T05HeroLeadForm } from '../../landing-templates/t05-launchdarkly/T05HeroLeadForm';
import { T05LeadModal } from '../../landing-templates/t05-launchdarkly/T05LeadModal';
import { useT05ProductLead } from '../../../lib/t05ProductLead';
import type { T05ProductPageProps } from './routes.fragment';
import './t05-product.css';

const ARKO_LEAD = { interest: 'ARKO – Demo', quelle: 't05-product-arko' };

const ARKO_NAV = [
  { href: '#funktionen', label: 'Funktionen' },
  { href: '#dokumente', label: 'Dokumente' },
  { href: '#diktat', label: 'Diktat' },
];

const HUB_MODES = [
  { id: 'recherche', label: 'Recherche', active: true },
  { id: 'struktur', label: 'Strukturieren' },
  { id: 'entwurf', label: 'Entwurf' },
  { id: 'akte', label: 'Aktenkontext' },
];

const DIKTAT_STEPS = [
  { label: 'Audio-Upload', badge: 'Portal/Matrix' },
  { label: 'Whisper large-v3', badge: 'Lokal' },
  { label: 'Mistral via Ollama', badge: 'Analyse' },
  { label: 'LEXCORE/BRAIN', badge: 'Kontext' },
  { label: 'Gotenberg PDF', badge: 'Vermerk' },
  { label: 'Nextcloud-Ablage', badge: 'Verschlüsselt' },
];

interface ProductArkoProps extends T05ProductPageProps {}

function ArkoChatScreen() {
  return (
    <div className="t05-prod-screen" aria-label="ARKO Chat — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">arko://session/recherche</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-modes" role="list" aria-label="ARKO Arbeitsmodi">
          {HUB_MODES.map((mode) => (
            <span
              key={mode.id}
              role="listitem"
              className={`t05-prod-mode${mode.active ? ' t05-prod-mode--active' : ''}`}
            >
              {mode.label}
            </span>
          ))}
        </div>
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--user">
          Welche Verjährungsfrist gilt für Schadenersatz nach § 1489 ABGB?
        </div>
        <div className="t05-prod-screen__chat-bubble t05-prod-screen__chat-bubble--arko">
          <strong style={{ color: 'var(--t05-paper)' }}>§ 1489 ABGB</strong> — Drei Jahre ab Kenntnis.
          <br />
          <span className="t05-product-panel__badge" style={{ marginTop: '0.5rem', display: 'inline-block' }}>
            LEXCORE · RIS
          </span>
        </div>
        <div className="t05-prod-screen__row">
          <span>Aktenkontext: Mandat Müller / Schadenersatz</span>
          <span className="t05-prod-screen__badge">Edge</span>
        </div>
      </div>
    </div>
  );
}

function OnlyOfficeSplitScreen() {
  return (
    <div className="t05-prod-screen" aria-label="OnlyOffice-Split — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">portal://mandant/akte/split</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-prod-screen__split">
          <div className="t05-prod-screen__pane">
            <p className="t05-prod-screen__pane-label">OnlyOffice</p>
            <p>Schriftsatz_Einrede.docx</p>
            <p style={{ marginTop: '0.75rem', opacity: 0.6 }}>
              …die Beklagte hat die vertragliche Leistung nicht erbracht…
            </p>
          </div>
          <div className="t05-prod-screen__pane">
            <p className="t05-prod-screen__pane-label">ARKO</p>
            <p>Formuliere eine Einrede der Verjährung für Absatz 2.</p>
            <p style={{ marginTop: '0.75rem', fontSize: '0.72rem', color: 'var(--t05-trust)' }}>
              ● Aktenkontext aktiv
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function LexcorePanel() {
  return (
    <div className="t05-product-panel">
      <div className="t05-product-panel__bar" aria-hidden>
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
        <span className="t05-product-panel__dot" />
      </div>
      <p className="t05-product-panel__title">lexcore://ris-kern</p>
      <div className="t05-product-panel__grid">
        {[
          { label: 'MRG § 23 — Kündigung', badge: 'RIS' },
          { label: 'ABGB § 1295', badge: 'Kuratiert' },
          { label: 'Kanzlei-Wissen', badge: 'BRAIN' },
          { label: 'OGH 4Ob123/24y', badge: 'Quelle' },
        ].map((row) => (
          <div key={row.label} className="t05-product-panel__row">
            <span>{row.label}</span>
            <span className="t05-product-panel__badge">{row.badge}</span>
            <span className="t05-product-panel__status" aria-hidden />
          </div>
        ))}
      </div>
    </div>
  );
}

function DiktatPipelineScreen() {
  return (
    <div className="t05-prod-screen" aria-label="Diktat-Pipeline — Platzhalter">
      <div className="t05-prod-screen__chrome">
        <div className="t05-prod-screen__dots" aria-hidden>
          <span />
          <span />
          <span />
        </div>
        <span className="t05-prod-screen__url">diktat://pipeline</span>
      </div>
      <div className="t05-prod-screen__body">
        <div className="t05-product-panel__grid">
          {DIKTAT_STEPS.map((step) => (
            <div key={step.label} className="t05-product-panel__row">
              <span>{step.label}</span>
              <span className="t05-product-panel__badge">{step.badge}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export const ProductArko: React.FC<ProductArkoProps> = ({ onLoginClick, onLeadSubmit }) => {
  useRevealOnScroll();

  const lead = useT05ProductLead({ onLeadSubmit }, ARKO_LEAD);
  const openDemo = () => lead.openDemo(ARKO_LEAD.interest);

  return (
    <T05ProductShell
      productLabel="ARKO"
      navAnchors={ARKO_NAV}
      onLoginClick={onLoginClick}
      onDemoClick={openDemo}
    >
      <header className="t05-hero t05-hero--product t05-hero--compact">
        <p className="t05-eyebrow reveal-up">KI-Rechtsassistent · Edge · EU</p>
        <h1 className="t05-headline t05-headline--product reveal-up">
          ARKO.
          <span className="t05-headline__accent">Mit Kontext.</span>
        </h1>
        <p className="t05-lead reveal-up">
          Chat im Portal mit Mandats- und Dokumentkontext. Recherche, Strukturieren, Entwürfe —
          angebunden an BRAIN/LEXCORE, <strong>lokal auf Ihrem Server</strong>.
        </p>
        <T05HeroLeadForm lead={lead} />
      </header>

      <section className="t05-container t05-refero-features" aria-label="ARKO Funktionen">
        <T05ReferoFeatureRow
          id="funktionen"
          accent="coral"
          label="Hub-Modi"
          labelIcon={Sparkles}
          title="Ein Assistent, vier Arbeitsmodi"
          description="ARKO passt sich Ihrem Arbeitsschritt an — von der Normrecherche bis zum strukturierten Entwurf, mit quellenbelegten Antworten aus LEXCORE."
          bullets={[
            { icon: Search, text: 'Recherche mit quellenbelegten Antworten aus LEXCORE' },
            { icon: MessageSquare, text: 'Strukturieren komplexer Sachverhalte im Chat' },
            { icon: FileText, text: 'Entwürfe mit Blick auf Ihr Mandat — kein Blindtext' },
            { icon: Brain, text: 'Aktenkontext: Dokumente und BRAIN-Wissen einbeziehen' },
          ]}
          visual={<ArkoChatScreen />}
        />

        <T05ReferoFeatureRow
          id="dokumente"
          reverse
          accent="voltage"
          label="Dokumentkontext"
          labelIcon={SplitSquareHorizontal}
          title="Akte im Blick — OnlyOffice-Split"
          description="ARKO kennt Ihre geöffnete Akte. OnlyOffice und Chat nebeneinander — bearbeiten und recherchieren ohne Tool-Wechsel, Speichern auf Nextcloud."
          bullets={[
            { icon: SplitSquareHorizontal, text: 'Split-View: Dokument links, ARKO rechts' },
            { icon: FileText, text: 'OnlyOffice im Browser — kein Export zu US-Cloud-Diensten' },
            { icon: Check, text: 'Aktenkontext bleibt während der Bearbeitung aktiv' },
          ]}
          footer={
            <Link to="/produkt/orbis" className="t05-stack-item__cta">
              Orbis-Tarife <ArrowRight size={16} aria-hidden />
            </Link>
          }
          visual={<OnlyOfficeSplitScreen />}
        />

        <T05ReferoFeatureRow
          accent="gold"
          label="BRAIN / LEXCORE"
          labelIcon={BookOpen}
          title={
            <>
              Rechtskern &amp; <em>Kanzlei-Wissen</em>
            </>
          }
          description="LEXCORE liefert den kuratierten österreichischen Rechtskern. BRAIN durchsucht Ihr kanzlei-eigenes Wissen — beides auf Ihrem Edge-Server, jede Norm mit Beleg."
          bullets={[
            { icon: BookOpen, text: 'Jede zitierte Norm mit Beleg — keine Vollständigkeitsgarantie' },
            { icon: Brain, text: 'Vektorsuche über Ihre Dokumente und Präzedenzfälle' },
          ]}
          visual={<LexcorePanel />}
        />

        <T05ReferoFeatureRow
          id="diktat"
          reverse
          accent="trust"
          label="Diktat & Transkription"
          labelIcon={Workflow}
          title="Sprache → Aktenvermerk-PDF"
          description={`Baustein von ${PRODUCT_NAME_MARKETING} — Teil der All-in-One-Plattform. Whisper, Ollama und Gotenberg laufen lokal; 0 % Daten verlassen Ihren Server.`}
          bullets={[
            { icon: Mic, text: 'Upload per Portal oder Matrix (E2EE)' },
            { icon: Check, text: 'Whisper, Ollama, Gotenberg — alles auf dem Edge-Server' },
            { icon: Check, text: 'Ablage in Nextcloud, temporäre Dateien gelöscht' },
          ]}
          visual={<DiktatPipelineScreen />}
        />
      </section>

      <section className="t05-prod-section--tight t05-container reveal-up">
        <div className="t05-prod-disclaimer">
          <strong>Hinweis:</strong> ARKO ist ein Werkzeug für Recherche, Strukturierung und Entwürfe —
          kein Ersatz für die anwaltliche Prüfung. Verantwortung und Entscheidung bleiben bei Ihnen.
        </div>
      </section>

      <section className="t05-final-cta t05-container reveal-up">
        <div className="t05-final-cta__band">
          <div className="t05-final-cta__inner">
            <p className="t05-kicker">Teil von Orbis</p>
            <h2 className="t05-title">ARKO auf Ihrem Edge-Server</h2>
            <p className="t05-subtitle">
              Teil der All-in-One-Plattform — Self-Hosted, ein Login, volle Kontrolle.
            </p>
            <div className="t05-cta-actions">
              <button type="button" className="t05-btn t05-btn--primary" onClick={openDemo}>
                Demo anfragen
              </button>
              <Link to="/produkt/orbis" className="t05-btn t05-btn--ghost">
                Orbis ansehen
              </Link>
            </div>
          </div>
        </div>
      </section>
      <T05LeadModal lead={lead} />
    </T05ProductShell>
  );
};
